import React, { createContext, useContext, useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import axios from 'axios';
import { Toaster } from '@/components/ui/sonner';
import '@/App.css';

// Pages
import HomePage from './pages/HomePage';
import ProductsPage from './pages/ProductsPage';
import ProductDetailPage from './pages/ProductDetailPage';
import CartPage from './pages/CartPage';
import CheckoutPage from './pages/CheckoutPage';
import AuthPage from './pages/AuthPage';
import DashboardPage from './pages/DashboardPage';
import PaymentSuccessPage from './pages/PaymentSuccessPage';
import UnsubscribePage from './pages/UnsubscribePage';
import BookAppointmentPage from './pages/BookAppointmentPage';
import ManageAppointmentPage from './pages/ManageAppointmentPage';

// Admin Pages
import AdminLoginPage from './pages/admin/AdminLoginPage';
import AdminDashboard from './pages/admin/AdminDashboard';
import AdminProductsPage from './pages/admin/AdminProductsPage';
import AdminOrdersPage from './pages/admin/AdminOrdersPage';
import AdminCustomersPage from './pages/admin/AdminCustomersPage';
import AdminSettingsPage from './pages/admin/AdminSettingsPage';
import AdminAppointmentsPage from './pages/admin/AdminAppointmentsPage';
import AdminCategoriesPage from './pages/admin/AdminCategoriesPage';

// Components
import Navbar from './components/Navbar';
import Footer from './components/Footer';
import AdminLayout from './components/AdminLayout';
import WhatsAppButton from './components/WhatsAppButton';

// When the app is served from a custom production domain (e.g. ambikafashion.in),
// always call the backend on the same origin to avoid cross-origin CORS / cookie
// issues that can cause "Login failed" errors. In preview / dev environments we
// use the explicit REACT_APP_BACKEND_URL.
const _IS_BROWSER = typeof window !== 'undefined' && !!window.location;
const _ENV_BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const _CURRENT_HOST = _IS_BROWSER ? window.location.host : '';
const _IS_INTERNAL_HOST = /(?:^|\.)emergent(?:agent)?\.(?:com|host)$|^localhost(?::\d+)?$|^127\.0\.0\.1/i.test(_CURRENT_HOST);
const BACKEND_URL = (_IS_BROWSER && !_IS_INTERNAL_HOST)
  ? `${window.location.protocol}//${window.location.host}`
  : _ENV_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Auth Context
const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadUser = async () => {
      if (token) {
        try {
          const response = await axios.get(`${API}/auth/me`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          setUser(response.data);
        } catch (error) {
          console.error('Failed to load user', error);
          if (error.response?.status === 401) {
            localStorage.removeItem('token');
            setToken(null);
            setUser(null);
          }
        }
      }
      setLoading(false);
    };
    loadUser();
  }, [token]);

  // Add axios interceptor to handle 401 errors globally
  useEffect(() => {
    const interceptor = axios.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.response?.status === 401 && token) {
          // Token expired or invalid
          localStorage.removeItem('token');
          setToken(null);
          setUser(null);
        }
        return Promise.reject(error);
      }
    );

    return () => {
      axios.interceptors.response.eject(interceptor);
    };
  }, [token]);

  const login = (newToken, userData) => {
    localStorage.setItem('token', newToken);
    setToken(newToken);
    setUser(userData);
  };

  const logout = () => {
    localStorage.removeItem('token');
    setToken(null);
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, token, login, logout, loading }}>
      {children}
    </AuthContext.Provider>
  );
};

const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return <div className="min-h-screen flex items-center justify-center">Loading...</div>;
  }
  
  if (!user) {
    return <Navigate to="/auth" replace />;
  }
  
  return children;
};

// Admin Auth Context
const AdminAuthContext = createContext(null);

export const useAdminAuth = () => {
  const context = useContext(AdminAuthContext);
  if (!context) {
    throw new Error('useAdminAuth must be used within AdminAuthProvider');
  }
  return context;
};

const AdminAuthProvider = ({ children }) => {
  const [admin, setAdmin] = useState(null);
  const [adminToken, setAdminToken] = useState(localStorage.getItem('adminToken'));
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadAdmin = async () => {
      if (adminToken) {
        try {
          const response = await axios.get(`${API}/admin/auth/me`, {
            headers: { Authorization: `Bearer ${adminToken}` }
          });
          setAdmin(response.data);
        } catch (error) {
          console.error('Failed to load admin', error);
          if (error.response?.status === 401) {
            localStorage.removeItem('adminToken');
            setAdminToken(null);
            setAdmin(null);
          }
        }
      }
      setLoading(false);
    };
    loadAdmin();
  }, [adminToken]);

  const adminLogin = (newToken, adminData) => {
    localStorage.setItem('adminToken', newToken);
    setAdminToken(newToken);
    setAdmin(adminData);
  };

  const adminLogout = () => {
    localStorage.removeItem('adminToken');
    setAdminToken(null);
    setAdmin(null);
  };

  return (
    <AdminAuthContext.Provider value={{ admin, adminToken, adminLogin, adminLogout, loading }}>
      {children}
    </AdminAuthContext.Provider>
  );
};

const ProtectedAdminRoute = ({ children }) => {
  const { admin, loading } = useAdminAuth();
  
  if (loading) {
    return <div className="min-h-screen flex items-center justify-center">Loading...</div>;
  }
  
  if (!admin) {
    return <Navigate to="/admin/login" replace />;
  }
  
  return children;
};

function App() {
  const [adminTokenState, setAdminTokenState] = useState(localStorage.getItem('adminToken'));

  const handleAdminLogin = (token, adminData) => {
    localStorage.setItem('adminToken', token);
    setAdminTokenState(token);
  };

  const handleAdminLogout = () => {
    localStorage.removeItem('adminToken');
    setAdminTokenState(null);
  };

  return (
    <AuthProvider>
      <AdminAuthProvider>
        <BrowserRouter>
          <div className="App">
            <Routes>
              {/* Public Routes */}
              <Route path="/" element={<><Navbar /><HomePage /><Footer /></>} />
              <Route path="/products" element={<><Navbar /><ProductsPage /><Footer /></>} />
              <Route path="/products/:id" element={<><Navbar /><ProductDetailPage /><Footer /></>} />
              <Route path="/auth" element={<><Navbar /><AuthPage /><Footer /></>} />
              <Route path="/login" element={<Navigate to="/auth" replace />} />
              <Route path="/unsubscribe" element={<><Navbar /><UnsubscribePage /><Footer /></>} />
              <Route path="/book" element={<><Navbar /><BookAppointmentPage /><Footer /></>} />
              <Route path="/book/manage/:token" element={<><Navbar /><ManageAppointmentPage /><Footer /></>} />
              
              {/* Protected Customer Routes */}
              <Route path="/cart" element={<><Navbar /><ProtectedRoute><CartPage /></ProtectedRoute><Footer /></>} />
              <Route path="/checkout" element={<><Navbar /><ProtectedRoute><CheckoutPage /></ProtectedRoute><Footer /></>} />
              <Route path="/dashboard" element={<><Navbar /><ProtectedRoute><DashboardPage /></ProtectedRoute><Footer /></>} />
              <Route path="/payment-success" element={<><Navbar /><ProtectedRoute><PaymentSuccessPage /></ProtectedRoute><Footer /></>} />
              
              {/* Admin Routes */}
              <Route path="/admin/login" element={<AdminLoginPage onLogin={handleAdminLogin} />} />
              <Route 
                path="/admin/dashboard" 
                element={
                  <ProtectedAdminRoute>
                    <AdminLayout onLogout={handleAdminLogout}>
                      <AdminDashboard adminToken={adminTokenState} />
                    </AdminLayout>
                  </ProtectedAdminRoute>
                } 
              />
              <Route 
                path="/admin/products" 
                element={
                  <ProtectedAdminRoute>
                    <AdminLayout onLogout={handleAdminLogout}>
                      <AdminProductsPage adminToken={adminTokenState} />
                    </AdminLayout>
                  </ProtectedAdminRoute>
                } 
              />
              <Route 
                path="/admin/categories" 
                element={
                  <ProtectedAdminRoute>
                    <AdminLayout onLogout={handleAdminLogout}>
                      <AdminCategoriesPage adminToken={adminTokenState} />
                    </AdminLayout>
                  </ProtectedAdminRoute>
                } 
              />
              <Route 
                path="/admin/orders" 
                element={
                  <ProtectedAdminRoute>
                    <AdminLayout onLogout={handleAdminLogout}>
                      <AdminOrdersPage adminToken={adminTokenState} />
                    </AdminLayout>
                  </ProtectedAdminRoute>
                } 
              />
              <Route 
                path="/admin/customers" 
                element={
                  <ProtectedAdminRoute>
                    <AdminLayout onLogout={handleAdminLogout}>
                      <AdminCustomersPage adminToken={adminTokenState} />
                    </AdminLayout>
                  </ProtectedAdminRoute>
                } 
              />
              <Route 
                path="/admin/settings" 
                element={
                  <ProtectedAdminRoute>
                    <AdminLayout onLogout={handleAdminLogout}>
                      <AdminSettingsPage adminToken={adminTokenState} />
                    </AdminLayout>
                  </ProtectedAdminRoute>
                } 
              />
              <Route 
                path="/admin/appointments" 
                element={
                  <ProtectedAdminRoute>
                    <AdminLayout onLogout={handleAdminLogout}>
                      <AdminAppointmentsPage adminToken={adminTokenState} />
                    </AdminLayout>
                  </ProtectedAdminRoute>
                } 
              />
            </Routes>
            <Toaster position="top-center" />
            <WhatsAppButton />
          </div>
        </BrowserRouter>
      </AdminAuthProvider>
    </AuthProvider>
  );
}

export default App;
export { API };
